import { formatUserFacingError, withErrorCode } from '@/lib/errors'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, LayoutGrid, List, Search, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useProduction } from '../hooks/useProduction'
import {
  CURATION_COLUMNS,
  useCurationSubmissions,
  type CurationColumnId,
} from '../hooks/useCurationSubmissions'
import { CurationBoardView } from '../components/CurationBoardView'
import { CurationListView } from '../components/CurationListView'
import { SubmissionDetailModal } from '../components/SubmissionDetailModal'
import { selectionWorkflow, INVITATION_LABEL } from '@/types/selectionWorkflow'
import { InvitationProgress } from '../components/InvitationStatus'
import type { FinalizationPreview } from '@/types/productionRequirements'
import { finalizeSelectionsFn, previewFinalizeSelectionsFn } from '@/lib/firebase/functions'
import type { Submission } from '../hooks/useCurationSubmissions'

type ViewKey = 'board' | 'list'
const VIEW_STORAGE_KEY = 'overture.curationView'

export function CurationPage() {
  const { id } = useParams<{ id: string }>()
  const productionId = id ?? null
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    production,
    loading: productionLoading,
    error: productionError,
  } = useProduction(productionId)
  const {
    submissions,
    grouped,
    loading,
    error,
    moveSubmission,
    updateCuratorNotes,
    refresh,
    pendingSaves,
    current,
    liveError,
  } = useCurationSubmissions(productionId)

  const workflow = selectionWorkflow(production ?? {}, submissions)

  const [view, setView] = useState<ViewKey>(() => readInitialView(searchParams))
  const [search, setSearch] = useState('')
  const [finalizeOpen, setFinalizeOpen] = useState(false)
  const [preview, setPreview] = useState<FinalizationPreview | null>(null)
  const [previewError, setPreviewError] = useState('')
  const [previewTick, setPreviewTick] = useState(0)
  const [finalizing, setFinalizing] = useState(false)
  const canSend =
    workflow.canSend && current && !loading && !liveError && pendingSaves === 0 && !finalizing
  const [finalizeResult, setFinalizeResult] = useState<string | null>(null)
  const [detailSubmissionId, setDetailSubmissionId] = useState<string | null>(null)

  const detailSubmission: Submission | null = detailSubmissionId
    ? (submissions.find((s) => s.id === detailSubmissionId) ?? null)
    : null

  useEffect(() => {
    localStorage.setItem(VIEW_STORAGE_KEY, view)
    const next = new URLSearchParams(searchParams)
    next.set('view', view)
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  // Filter inside each column on search query
  const filteredGrouped = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return grouped
    const next = new Map<
      CurationColumnId,
      typeof grouped extends Map<CurationColumnId, infer V> ? V : never
    >()
    for (const [col, list] of grouped) {
      next.set(
        col,
        list.filter((s) => {
          const hay =
            `${s.applicantInfo.firstName} ${s.applicantInfo.lastName} ${s.applicantInfo.email} ${s.curatorNotes ?? ''}`.toLowerCase()
          return hay.includes(q)
        })
      )
    }
    return next as typeof grouped
  }, [grouped, search])

  const counts = useMemo(() => {
    const out: Record<CurationColumnId, number> = {
      reviewing: 0,
      accepted: 0,
      waitlisted: 0,
      rejected: 0,
    }
    for (const c of CURATION_COLUMNS) out[c.id] = grouped.get(c.id)?.length ?? 0
    return out
  }, [grouped])

  /**
   * Flat ordered list of submissions across all columns in the currently
   * filtered view. Drives prev/next navigation inside the detail modal —
   * curators walk through submissions in the same order they appear on
   * screen, in column config order (Reviewing → Accepted → Waitlisted → Rejected).
   */
  const flatOrdered = useMemo(() => {
    const out: Submission[] = []
    for (const c of CURATION_COLUMNS) {
      const list = filteredGrouped.get(c.id) ?? []
      out.push(...list)
    }
    return out
  }, [filteredGrouped])

  const currentIndex = detailSubmissionId
    ? flatOrdered.findIndex((s) => s.id === detailSubmissionId)
    : -1
  const prevSubmissionId = currentIndex > 0 ? (flatOrdered[currentIndex - 1]?.id ?? null) : null
  const nextSubmissionId =
    currentIndex >= 0 && currentIndex < flatOrdered.length - 1
      ? (flatOrdered[currentIndex + 1]?.id ?? null)
      : null

  useEffect(() => {
    let cancelled = false
    setPreview(null)
    setPreviewError('')
    if (!finalizeOpen || !productionId || pendingSaves || !current || finalizing) return
    void previewFinalizeSelectionsFn({ productionId })
      .then((result) => {
        if (!cancelled) setPreview(result.data)
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setPreviewError(formatUserFacingError(err, 'Could not prepare the invitation preview.'))
      })
    return () => {
      cancelled = true
    }
  }, [
    finalizeOpen,
    productionId,
    pendingSaves,
    submissions,
    production,
    previewTick,
    current,
    finalizing,
  ])

  async function finalize() {
    if (!productionId || !preview?.workflow.canSend || !canSend) return
    setFinalizing(true)
    setFinalizeResult(null)
    try {
      const result = await finalizeSelectionsFn({
        productionId,
        previewFingerprint: preview.fingerprint,
      })
      const {
        confirmedCount,
        skippedCount,
        failedCount,
        pendingCount = 0,
        uncertainCount = 0,
      } = result.data
      const resultMessage =
        `Email provider accepted ${confirmedCount} invitation${confirmedCount === 1 ? '' : 's'}.` +
        (skippedCount ? ` Skipped ${skippedCount} previously handled invitation(s).` : '') +
        (failedCount ? ` ${failedCount} could not be processed; review before retrying.` : '') +
        (pendingCount ? ` ${pendingCount} pending automatic delivery/retry.` : '') +
        (uncertainCount ? ` ${uncertainCount} need provider reconciliation before any resend.` : '')
      setFinalizeResult(
        failedCount > 0 ? withErrorCode(resultMessage, 'production/finalize-failed') : resultMessage
      )
      setFinalizeOpen(false)
      refresh()
    } catch (err) {
      setFinalizeResult(formatUserFacingError(err, 'Failed to finalize.'))
      setPreview(null)
      setPreviewTick((n) => n + 1)
    } finally {
      setFinalizing(false)
    }
  }

  if (productionLoading) {
    return (
      <div className="space-y-4 py-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (productionError || !production) {
    return (
      <div className="space-y-3 py-6">
        <Link
          to="/admin/productions"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Productions
        </Link>
        <p className="text-sm text-destructive">
          {productionError ??
            withErrorCode(
              "We couldn't find this production. It may have been deleted.",
              'productions/not-found'
            )}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4 py-2">
      <div>
        <Link
          to={`/admin/productions/${id}`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {production.name}
        </Link>
        <h1 className="text-2xl font-bold">Curation Workspace</h1>
      </div>

      {/* Counts banner */}
      <div className="flex flex-wrap items-center gap-4 rounded-md border bg-muted/30 px-4 py-2 text-sm">
        {CURATION_COLUMNS.map((c) => (
          <span key={c.id} className="text-muted-foreground">
            <span className="font-semibold text-foreground">{counts[c.id]}</span> {c.title}
          </span>
        ))}
      </div>

      <InvitationProgress
        production={production}
        submissions={submissions}
        loading={loading}
        current={current}
        error={liveError}
      />
      <Link
        to={`/admin/productions/${id}?tab=participants`}
        className="inline-flex min-h-11 items-center text-sm underline"
      >
        View participant responses and materials
      </Link>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search applicants, emails, notes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>

        {/* View segmented toggle */}
        <div className="flex rounded-md border p-0.5">
          <Button
            variant={view === 'board' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setView('board')}
            className="h-7"
          >
            <LayoutGrid className="mr-1 h-4 w-4" /> Board
          </Button>
          <Button
            variant={view === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setView('list')}
            className="h-7"
          >
            <List className="mr-1 h-4 w-4" /> List
          </Button>
        </div>

        <Button
          onClick={() => {
            setFinalizeResult(null)
            setFinalizeOpen(true)
          }}
          disabled={!canSend}
        >
          <Check className="mr-1 h-4 w-4" />
          {loading
            ? 'Checking invitations…'
            : workflow.state === 'sent'
              ? `Acceptances sent (${workflow.counts.sent})`
              : workflow.processing && !workflow.newInvitationCount
                ? 'Invitations processing…'
                : workflow.newInvitationCount
                  ? `Review new acceptances (${workflow.newInvitationCount})`
                  : 'No new acceptances to send'}
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {finalizeResult && (
        <p className="rounded border bg-muted/30 px-3 py-2 text-sm">{finalizeResult}</p>
      )}

      {loading ? (
        <Skeleton className="h-64 w-full" />
      ) : view === 'board' ? (
        <CurationBoardView
          grouped={filteredGrouped}
          onMove={moveSubmission}
          onNotesChange={updateCuratorNotes}
          onOpenDetail={(s) => setDetailSubmissionId(s.id)}
        />
      ) : (
        <CurationListView
          grouped={filteredGrouped}
          onMove={moveSubmission}
          onNotesChange={updateCuratorNotes}
          onOpenDetail={(s) => setDetailSubmissionId(s.id)}
        />
      )}

      <SubmissionDetailModal
        submission={detailSubmission}
        onClose={() => setDetailSubmissionId(null)}
        onNotesChange={updateCuratorNotes}
        onMove={moveSubmission}
        onPrev={prevSubmissionId ? () => setDetailSubmissionId(prevSubmissionId) : null}
        onNext={nextSubmissionId ? () => setDetailSubmissionId(nextSubmissionId) : null}
        position={currentIndex >= 0 ? { index: currentIndex + 1, total: flatOrdered.length } : null}
      />

      <Dialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-auto">
          <DialogHeader>
            <DialogTitle>Review new acceptances</DialogTitle>
            <DialogDescription>
              Only applicants marked Not sent receive a new acceptance. Queued invitations continue
              automatically. Sent, cancelled, and uncertain invitations are never resent here.
            </DialogDescription>
          </DialogHeader>
          {finalizeResult && (
            <p role="alert" className="text-sm text-destructive">
              {finalizeResult}
            </p>
          )}
          {previewError && (
            <p role="alert" className="text-sm text-destructive">
              {previewError}
            </p>
          )}
          {!current && <p role="alert">Waiting for live invitation status. Sending is disabled.</p>}
          {!preview && !previewError && (
            <p>
              {finalizing
                ? 'Sending invitations. Progress is saved automatically…'
                : 'Preparing current recipient list…'}
            </p>
          )}
          {preview && (
            <div className="space-y-3">
              <p className="text-sm font-medium">
                {preview.recipients.length} selected · {preview.workflow.newInvitationCount} new
                acceptance emails to send
              </p>
              <ul className="max-h-40 overflow-auto text-sm">
                {preview.recipients.map((r) => (
                  <li key={r.submissionId}>
                    {r.name}, {r.email}
                    {` (${INVITATION_LABEL[r.invitationState]})`}
                  </li>
                ))}
              </ul>
              {preview.packageBacked ? (
                <p className="text-sm">
                  This production sends participant-package invitations using its configured price
                  and payment deadlines.
                </p>
              ) : (
                <>
                  <p className="text-sm font-medium">Subject: {preview.subject}</p>
                  <iframe
                    title="Invitation email preview"
                    sandbox=""
                    srcDoc={preview.html}
                    className="h-80 w-full rounded border bg-white"
                  />
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={finalizing}
              onClick={() => setPreviewTick((n) => n + 1)}
            >
              Refresh preview
            </Button>
            <Button variant="outline" onClick={() => setFinalizeOpen(false)}>
              Cancel
            </Button>
            <Button onClick={finalize} disabled={!canSend || !preview?.workflow.canSend}>
              {finalizing
                ? 'Sending…'
                : preview?.workflow.newInvitationCount
                  ? `Send ${preview.workflow.newInvitationCount} new acceptance${preview.workflow.newInvitationCount === 1 ? '' : 's'}`
                  : 'No new acceptances to send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function readInitialView(searchParams: URLSearchParams): ViewKey {
  const fromUrl = searchParams.get('view')
  if (fromUrl === 'board' || fromUrl === 'list') return fromUrl
  const stored = typeof window !== 'undefined' ? localStorage.getItem(VIEW_STORAGE_KEY) : null
  if (stored === 'board' || stored === 'list') return stored
  return 'board'
}
